import React, { useEffect, useMemo, useRef, useState } from 'react';

import styles from './actSlotAdd.module.css';

import { HOURS, PERIODS, pad2, timeToMinutes } from './timeUtils';

const InlineTimePicker = ({
    value,
    onChange,

    minuteStep = 1,

    placeholder = 'Select Time',

    // --------------------------------------------------------
    // End Time only
    // --------------------------------------------------------
    // Example:
    // minTime="02:00 PM"
    //
    // End Time must be greater than this.
    minTime = null,

    // --------------------------------------------------------
    // Disable picker
    // --------------------------------------------------------
    disabled = false
}) => {
    // ========================================================
    // STATE
    // ========================================================

    const [isOpen, setIsOpen] = useState(false);

    const [hour, setHour] = useState(null);

    const [minute, setMinute] = useState(null);

    const [period, setPeriod] = useState(null);

    // ========================================================
    // REFS
    // ========================================================

    const wrapperRef = useRef(null);

    const hourListRef = useRef(null);

    const minuteListRef = useRef(null);

    const periodListRef = useRef(null);

    // ========================================================
    // MINUTES
    // ========================================================

    const minutes = useMemo(() => {
        const list = [];

        for (let m = 0; m < 60; m += minuteStep) {
            list.push(pad2(m));
        }

        return list;
    }, [minuteStep]);

    // ========================================================
    // PARSE VALUE
    // ========================================================

    useEffect(() => {
        if (!value) {
            setHour(null);
            setMinute(null);
            setPeriod(null);

            return;
        }

        const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);

        if (!match) {
            return;
        }

        setHour(pad2(parseInt(match[1], 10)));

        setMinute(pad2(parseInt(match[2], 10)));

        setPeriod(match[3].toUpperCase());
    }, [value]);

    // ========================================================
    // CLOSE WHEN CLICKING OUTSIDE
    // ========================================================

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // ========================================================
    // CLOSE WHEN DISABLED
    // ========================================================

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    // ========================================================
    // MIN START TIME
    // ========================================================

    const minTimeInMinutes = timeToMinutes(minTime);

    // ========================================================
    // VALIDATE COMPLETE TIME
    // ========================================================

    const isEndTimeValid = (time) => {
        // This is the Start Time picker
        if (!minTime) {
            return true;
        }

        const selectedTime = timeToMinutes(time);

        if (selectedTime === null) {
            return true;
        }

        if (minTimeInMinutes === null) {
            return true;
        }

        // IMPORTANT:
        // End Time must be strictly greater.
        //
        // Start 02:00 PM
        // End   02:00 PM -> invalid
        //
        // Start 02:00 PM
        // End   02:01 PM -> valid

        return selectedTime > minTimeInMinutes;
    };

    // ========================================================
    // CHECK HOUR
    // ========================================================

    const isHourDisabled = (selectedHour) => {
        // Start Time picker
        if (!minTime) {
            return false;
        }

        /*
         * An hour is disabled only when there is
         * absolutely no valid minute + period
         * combination for that hour.
         */

        const hasValidCombination = PERIODS.some((selectedPeriod) =>
            minutes.some((selectedMinute) => isEndTimeValid(`${selectedHour}:${selectedMinute} ${selectedPeriod}`))
        );

        return !hasValidCombination;
    };

    // ========================================================
    // CHECK MINUTE
    // ========================================================

    const isMinuteDisabled = (selectedMinute) => {
        if (!minTime) {
            return false;
        }

        /*
         * Until hour and period are selected,
         * don't disable minutes.
         */

        if (!hour || !period) {
            return false;
        }

        return !isEndTimeValid(`${hour}:${selectedMinute} ${period}`);
    };

    // ========================================================
    // CHECK PERIOD
    // ========================================================

    const isPeriodDisabled = (selectedPeriod) => {
        if (!minTime) {
            return false;
        }

        /*
         * Until hour and minute are selected,
         * don't disable AM/PM.
         */

        if (!hour || !minute) {
            return false;
        }

        return !isEndTimeValid(`${hour}:${minute} ${selectedPeriod}`);
    };

    // ========================================================
    // EMIT COMPLETE TIME
    // ========================================================

    const emitIfComplete = (selectedHour, selectedMinute, selectedPeriod) => {
        if (!selectedHour || !selectedMinute || !selectedPeriod) {
            return;
        }

        const newTime = `${selectedHour}:${selectedMinute} ${selectedPeriod}`;

        // End Time validation
        if (minTime && !isEndTimeValid(newTime)) {
            return;
        }

        if (onChange) {
            onChange(newTime);
        }

        setIsOpen(false);
    };

    // ========================================================
    // SELECT HOUR
    // ========================================================

    const handleSelectHour = (selectedHour) => {
        if (isHourDisabled(selectedHour)) {
            return;
        }

        setHour(selectedHour);

        /*
         * If minute and period are already selected,
         * try to complete the time.
         */

        if (minute && period) {
            const candidate = `${selectedHour}:${minute} ${period}`;

            if (!minTime || isEndTimeValid(candidate)) {
                emitIfComplete(selectedHour, minute, period);
            }
        }
    };

    // ========================================================
    // SELECT MINUTE
    // ========================================================

    const handleSelectMinute = (selectedMinute) => {
        if (isMinuteDisabled(selectedMinute)) {
            return;
        }

        setMinute(selectedMinute);

        if (hour && period) {
            emitIfComplete(hour, selectedMinute, period);
        }
    };

    // ========================================================
    // SELECT PERIOD
    // ========================================================

    const handleSelectPeriod = (selectedPeriod) => {
        if (isPeriodDisabled(selectedPeriod)) {
            return;
        }

        setPeriod(selectedPeriod);

        if (hour && minute) {
            emitIfComplete(hour, minute, selectedPeriod);
        }
    };

    // ========================================================
    // SCROLL SELECTED ITEM
    // ========================================================

    const scrollSelectedIntoView = (listRef, selectedValue) => {
        if (!listRef.current || !selectedValue) {
            return;
        }

        const selectedElement = listRef.current.querySelector(`[data-value="${selectedValue}"]`);

        if (!selectedElement) {
            return;
        }

        selectedElement.scrollIntoView({
            block: 'center',
            behavior: 'auto'
        });
    };

    // ========================================================
    // SCROLL TO SELECTED VALUES WHEN OPENING
    // ========================================================

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        /*
         * Wait until dropdown is rendered
         * before trying to scroll.
         */

        requestAnimationFrame(() => {
            scrollSelectedIntoView(hourListRef, hour);

            scrollSelectedIntoView(minuteListRef, minute);

            scrollSelectedIntoView(periodListRef, period);
        });
    }, [isOpen, hour, minute, period]);

    // ========================================================
    // DISPLAY VALUE
    // ========================================================

    const displayValue = hour && minute && period ? `${hour}:${minute} ${period}` : '';

    // ========================================================
    // RENDER
    // ========================================================

    return (
        <div ref={wrapperRef} className={`${styles.timePickerWrapper} ${disabled ? styles.timePickerDisabled : ''}`}>
            {/* ==================================================
                TRIGGER
            ================================================== */}

            <button
                type='button'
                className={styles.timePickerTrigger}
                disabled={disabled}
                onClick={() => {
                    if (disabled) {
                        return;
                    }

                    setIsOpen((prev) => !prev);
                }}
            >
                <span className={displayValue ? styles.timePickerValue : styles.timePickerPlaceholder}>
                    {displayValue || placeholder}
                </span>

                <span className={`${styles.timePickerChevron} ${isOpen ? styles.timePickerChevronOpen : ''}`}>▾</span>
            </button>

            {/* ==================================================
                DROPDOWN
            ================================================== */}

            {isOpen && (
                <div className={styles.timePickerDropdown}>
                    <div className={styles.timePickerColumns}>
                        {/* ======================================
                            HOURS
                        ====================================== */}

                        <div className={styles.timePickerColumn}>
                            <div className={styles.timePickerColumnLabel}>Hours</div>

                            <div ref={hourListRef} className={styles.timePickerList}>
                                {HOURS.map((h) => {
                                    const disabledHour = isHourDisabled(h);

                                    return (
                                        <button
                                            key={h}
                                            type='button'
                                            data-value={h}
                                            disabled={disabledHour}
                                            className={`${styles.timePickerItem} ${
                                                hour === h ? styles.timePickerItemSelected : ''
                                            } ${disabledHour ? styles.timePickerItemDisabled : ''}`}
                                            onClick={() => handleSelectHour(h)}
                                        >
                                            {h}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ======================================
                            MINUTES
                        ====================================== */}

                        <div className={styles.timePickerColumn}>
                            <div className={styles.timePickerColumnLabel}>Minutes</div>

                            <div ref={minuteListRef} className={styles.timePickerList}>
                                {minutes.map((m) => {
                                    const disabledMinute = isMinuteDisabled(m);

                                    return (
                                        <button
                                            key={m}
                                            type='button'
                                            data-value={m}
                                            disabled={disabledMinute}
                                            className={`${styles.timePickerItem} ${
                                                minute === m ? styles.timePickerItemSelected : ''
                                            } ${disabledMinute ? styles.timePickerItemDisabled : ''}`}
                                            onClick={() => handleSelectMinute(m)}
                                        >
                                            {m}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ======================================
                            PERIOD
                        ====================================== */}

                        <div className={styles.timePickerColumn}>
                            <div className={styles.timePickerColumnLabel}>Period</div>

                            <div ref={periodListRef} className={styles.timePickerList}>
                                {PERIODS.map((p) => {
                                    const disabledPeriod = isPeriodDisabled(p);

                                    return (
                                        <button
                                            key={p}
                                            type='button'
                                            data-value={p}
                                            disabled={disabledPeriod}
                                            className={`${styles.timePickerItem} ${styles.timePickerItemPeriod} ${
                                                period === p ? styles.timePickerItemSelected : ''
                                            } ${disabledPeriod ? styles.timePickerItemDisabled : ''}`}
                                            onClick={() => handleSelectPeriod(p)}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InlineTimePicker;
